import React, { useState, useEffect } from 'react';
import { 
  Phone, Mail, MapPin, Building, Clock, User, Copy, ExternalLink, 
  CheckCircle2, Trash2, DollarSign 
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { updateCrmContact, addCrmNote, deleteCrmNote, markContactAsContacted } from '../../../services/api';

const ContactDetailModal = ({ isOpen, contact, currentUser, onClose, onRefresh }) => {
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [markingContacted, setMarkingContacted] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    city: '',
    state: '',
    source: 'manual',
    summary: '',
    potential_value: ''
  });

  const sourceOptions = [
    { value: 'manual', label: 'Manual' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'whatsapp_link', label: 'Link WhatsApp' },
    { value: 'shopee', label: 'Shopee' },
    { value: 'indicacao', label: 'Indicação' },
    { value: 'site', label: 'Site' },
    { value: 'orcamento', label: 'Orçamento' },
    { value: 'outro', label: 'Outro' }
  ];

  const openWhatsAppDesktop = () => {
    const phone = form.phone.replace(/\D/g, '');
    const whatsappUrl = `whatsapp://send?phone=55${phone}`;
    window.location.href = whatsappUrl;
  };

  const openWhatsAppWeb = () => {
    const phone = form.phone.replace(/\D/g, '');
    window.open(`https://wa.me/55${phone}`, '_blank');
  };

  const copyPhoneToClipboard = () => {
    navigator.clipboard.writeText(form.phone);
    toast.success('Número copiado!');
  };

  const handleMarkContacted = async () => {
    setMarkingContacted(true);
    try {
      await markContactAsContacted(contact.id);
      toast.success(`${form.name} marcado como contatado!`);
      onRefresh();
    } catch (error) {
      toast.error('Erro ao marcar contato');
    } finally {
      setMarkingContacted(false);
    }
  };

  const isContactedToday = () => {
    if (!contact?.last_contacted_at) return false;
    const lastContact = new Date(contact.last_contacted_at);
    const today = new Date();
    return lastContact.toDateString() === today.toDateString();
  };

  const formatLastContacted = () => {
    if (!contact?.last_contacted_at) return null;
    const lastContact = new Date(contact.last_contacted_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (lastContact.toDateString() === today.toDateString()) {
      return 'Hoje';
    } else if (lastContact.toDateString() === yesterday.toDateString()) {
      return 'Ontem';
    } else {
      return lastContact.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    }
  };

  useEffect(() => {
    if (contact) {
      setForm({
        name: contact.name || '',
        phone: contact.phone || '',
        email: contact.email || '',
        company: contact.company || '',
        city: contact.city || '',
        state: contact.state || '',
        source: contact.source || 'manual',
        summary: contact.summary || '',
        potential_value: contact.potential_value || ''
      });
      setHasChanges(false);
    }
  }, [contact]);

  if (!contact) return null;

  const updateForm = (field, value) => {
    setForm(p => ({ ...p, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        ...form,
        potential_value: form.potential_value ? parseFloat(form.potential_value) : null
      };
      await updateCrmContact(contact.id, dataToSave);
      setHasChanges(false);
      toast.success('Contato salvo!');
      onRefresh();
    } catch (error) {
      toast.error('Erro ao salvar alterações');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await addCrmNote(contact.id, newNote, currentUser?.name);
      setNewNote('');
      onRefresh();
    } catch (error) {
      alert('Erro ao adicionar nota');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Remover esta nota?')) return;
    try {
      await deleteCrmNote(contact.id, noteId);
      onRefresh();
    } catch (error) {
      alert('Erro ao remover nota');
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('Você tem alterações não salvas. Deseja salvar antes de fechar?')) {
        handleSave().then(() => onClose());
        return;
      }
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <Input 
              value={form.name} 
              onChange={(e) => updateForm('name', e.target.value)}
              className="text-lg font-semibold border-0 border-b border-transparent hover:border-slate-300 focus:border-teal-500 rounded-none px-0 h-auto py-1 bg-transparent"
              placeholder="Nome do contato"
            />
            <div className="flex items-center gap-2 flex-shrink-0 ml-4">
              {contact.source === 'orcamento' && (
                <Badge className="bg-purple-100 text-purple-700 border-0">Via Orçamento</Badge>
              )}
              {hasChanges && (
                <Button 
                  onClick={handleSave} 
                  disabled={saving} 
                  size="sm"
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </Button>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-xl">
            <Button
              variant="outline"
              size="sm"
              onClick={openWhatsAppDesktop}
              className="flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-50"
            >
              <Phone className="w-4 h-4" />
              WhatsApp Desktop
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openWhatsAppWeb}
              className="flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-50"
            >
              <ExternalLink className="w-4 h-4" />
              WhatsApp Web
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={copyPhoneToClipboard}
              className="flex items-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <Copy className="w-4 h-4" />
              Copiar Número
            </Button>
            <Button
              variant={isContactedToday() ? "default" : "outline"}
              size="sm"
              onClick={handleMarkContacted}
              disabled={markingContacted}
              className={`flex items-center gap-2 ${
                isContactedToday() 
                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
                  : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" />
              {markingContacted ? 'Marcando...' : isContactedToday() ? 'Contatado Hoje' : 'Marcar Contato'}
            </Button>
          </div>

          {/* Last contacted info */}
          {contact.last_contacted_at && (
            <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
              isContactedToday() ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
            }`}>
              <CheckCircle2 className="w-4 h-4" />
              <span>Último contato: {formatLastContacted()}</span>
              <span className="text-xs opacity-70">
                ({new Date(contact.last_contacted_at).toLocaleString('pt-BR')})
              </span>
            </div>
          )}

          {/* Summary - Editable */}
          <div className="p-4 bg-amber-50 rounded-xl border-l-4 border-amber-400">
            <p className="text-sm font-medium text-amber-800 mb-2">📋 Resumo da Negociação</p>
            <Textarea 
              value={form.summary} 
              onChange={(e) => updateForm('summary', e.target.value)} 
              rows={2}
              placeholder="Ex: Interessado em 500 canecas personalizadas..."
              className="bg-white border-amber-200 focus:border-amber-400"
            />
          </div>

          {/* Editable Contact Info */}
          <div className="grid grid-cols-2 gap-3 p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <Input 
                value={form.phone} 
                onChange={(e) => updateForm('phone', e.target.value)}
                placeholder="Telefone"
                className="h-8 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <div className="flex gap-1 flex-1">
                <Input 
                  value={form.city} 
                  onChange={(e) => updateForm('city', e.target.value)}
                  placeholder="Cidade"
                  className="h-8 text-sm flex-1"
                />
                <Input 
                  value={form.state} 
                  onChange={(e) => updateForm('state', e.target.value)}
                  placeholder="UF"
                  maxLength={2}
                  className="h-8 text-sm w-14"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="text-sm text-slate-600">{new Date(contact.created_at).toLocaleDateString('pt-BR')}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <select
                value={form.source}
                onChange={(e) => updateForm('source', e.target.value)}
                className="h-8 px-2 text-sm border border-slate-200 rounded-lg flex-1"
              >
                {sourceOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <Input 
                type="email"
                value={form.email} 
                onChange={(e) => updateForm('email', e.target.value)}
                placeholder="E-mail"
                className="h-8 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <Input 
                value={form.company} 
                onChange={(e) => updateForm('company', e.target.value)}
                placeholder="Empresa"
                className="h-8 text-sm"
              />
            </div>
            <div className="flex items-center gap-2 col-span-2">
              <DollarSign className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span className="text-sm text-slate-600">R$</span>
              <Input 
                type="number"
                step="0.01"
                value={form.potential_value} 
                onChange={(e) => updateForm('potential_value', e.target.value)}
                placeholder="Valor potencial"
                className="h-8 text-sm w-32"
              />
            </div>
          </div>

          {/* Quotes */}
          {contact.total_quotes > 0 && (
            <div className="p-4 bg-teal-50 rounded-xl">
              <p className="font-medium text-teal-800">
                {contact.total_quotes} orçamento(s) solicitado(s)
              </p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Observações</label>
            
            <div className="flex gap-2 mb-4">
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Adicionar observação..."
                rows={2}
                className="flex-1"
              />
              <Button 
                onClick={handleAddNote} 
                disabled={addingNote || !newNote.trim()}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                {addingNote ? '...' : 'Adicionar'}
              </Button>
            </div>

            <div className="space-y-3 max-h-48 overflow-y-auto">
              {contact.notes?.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Nenhuma observação ainda</p>
              ) : (
                contact.notes?.map((note) => (
                  <div key={note.id} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <p className="text-slate-700">{note.content}</p>
                    <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                      <span>
                        {note.created_by && `${note.created_by} • `}
                        {new Date(note.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                      <button 
                        onClick={() => handleDeleteNote(note.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={openWhatsAppDesktop}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-medium"
            >
              <Phone className="w-5 h-5" />
              WhatsApp Desktop
            </button>
            <button
              onClick={copyPhoneToClipboard}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-medium"
            >
              <Copy className="w-5 h-5" />
              Copiar
            </button>
            <Button variant="outline" onClick={handleClose} className="px-6">
              Fechar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDetailModal;
